/**
 * usePeerConnection -- create a PartyTracks instance + observe its
 * peerConnection. All SFU traffic (push local, pull remote) goes through this
 * single peer connection.
 *
 * PartyTracks `prefix` points at the worker's SFU proxy (config.sfuApiBase);
 * the worker attaches the App Secret. TURN creds come from the worker's
 * /cfcalls/turn-credentials (partytracks' built-in discovery doesn't work
 * against Cloudflare Realtime TURN), fetched with the caller's service-auth
 * bearer via callFetch.
 *
 * Ported from batesky-electron cfcalls: swapped apiFetch->callFetch,
 * config->local config, and lazy-loaded PartyTracks (webpack code-split).
 */

import {useEffect, useMemo, useState} from 'react'
import {useObservableAsValue} from 'partytracks/react'
import {EMPTY} from 'rxjs'

import {type CallFetch} from '../callFetch'
import {cfcallsConfig} from '../config'
import {
  loadPartyTracks,
  type PartyTracks,
  type PartyTracksClientModule,
} from './partytracks'

const FALLBACK_ICE_SERVERS = [{urls: 'stun:stun.cloudflare.com:3478'}]

export function usePeerConnection({
  callFetch,
  token,
}: {
  callFetch: CallFetch
  /** Current service-auth JWT (for the partytracks Headers). */
  token: string | null
}): {
  partyTracks: PartyTracks | null
  peerConnection: RTCPeerConnection | undefined
  sessionId: string | null
  connectionState: string
} {
  // A mutable Headers instance partytracks re-reads on every request; we keep
  // the same object and update the bearer in place so token refresh is picked
  // up without recreating PartyTracks. (This is the documented case where a
  // stable ref matters -- a callback into a non-React lib.)
  const stableHeaders = useMemo(() => new Headers(), [])
  useEffect(() => {
    if (token) stableHeaders.set('Authorization', `Bearer ${token}`)
    else stableHeaders.delete('Authorization')
  }, [token, stableHeaders])

  // Fetch TURN creds once we can authenticate.
  const [iceServers, setIceServers] = useState<RTCIceServer[] | null>(null)
  const hasToken = Boolean(token)
  useEffect(() => {
    if (!hasToken) return
    let cancelled = false
    const run = async () => {
      try {
        const res = await callFetch(cfcallsConfig.turnCredentialsPath, {
          method: 'POST',
        })
        const body = (await res.json()) as {iceServers?: RTCIceServer[]}
        if (cancelled) return
        const fetched = body?.iceServers
        setIceServers(
          Array.isArray(fetched) && fetched.length
            ? fetched
            : FALLBACK_ICE_SERVERS,
        )
      } catch {
        if (!cancelled) setIceServers(FALLBACK_ICE_SERVERS)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [hasToken, callFetch])

  // Lazily load partytracks, then construct once iceServers are ready.
  const [ptModule, setPtModule] = useState<PartyTracksClientModule | null>(null)
  useEffect(() => {
    let cancelled = false
    void loadPartyTracks().then(m => {
      if (!cancelled) setPtModule(m)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const partyTracks = useMemo(() => {
    if (!ptModule || !iceServers) return null
    return new ptModule.PartyTracks({
      prefix: cfcallsConfig.sfuApiBase,
      headers: stableHeaders,
      iceServers,
    })
  }, [ptModule, iceServers, stableHeaders])

  const peerConnection = useObservableAsValue<RTCPeerConnection | undefined>(
    partyTracks ? partyTracks.peerConnection$ : EMPTY,
  )
  const session = useObservableAsValue<{sessionId: string} | undefined>(
    partyTracks ? partyTracks.session$ : EMPTY,
  )

  const [connectionState, setConnectionState] = useState('new')
  useEffect(() => {
    if (!peerConnection) return
    const sync = () => setConnectionState(peerConnection.connectionState)
    // Reflect the current state on the next tick (avoids a synchronous
    // setState inside the effect body) plus on every change.
    const id = setTimeout(sync, 0)
    peerConnection.addEventListener('connectionstatechange', sync)
    return () => {
      clearTimeout(id)
      peerConnection.removeEventListener('connectionstatechange', sync)
    }
  }, [peerConnection])

  return {
    partyTracks,
    peerConnection,
    sessionId: session ? session.sessionId : null,
    connectionState,
  }
}
